import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, Gift, Copy, Check, Users, CreditCard,
  Clock, CheckCircle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReferralPage() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchStats = useCallback(async () => {
    try {
      // Get or generate referral link
      const linkRes = await fetch(`${API}/referral/link`, { headers });
      if (linkRes.ok) await linkRes.json();

      // Get stats
      const statsRes = await fetch(`${API}/referral/stats`, { headers });
      if (statsRes.ok) setStats(await statsRes.json());
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const referralUrl = stats?.referral_code
    ? `${window.location.origin}/auth?ref=${stats.referral_code}`
    : '';

  const handleCopy = () => {
    navigator.clipboard.writeText(referralUrl);
    setCopied(true);
    toast.success('Lien copie !');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0C0C0C] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4A853] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0C0C0C] p-4 sm:p-6" data-testid="referral-page">
      {/* Header */}
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/dashboard" className="p-2 rounded-xl bg-[#1A1A1A] border border-[#2A2A2A] hover:bg-[#222] transition-colors" data-testid="referral-back">
            <ArrowLeft className="w-5 h-5 text-white" />
          </Link>
          <div>
            <h1 className="text-xl font-bold text-white">Programme de Parrainage</h1>
            <p className="text-xs text-[#888]">Invitez et gagnez des mois gratuits</p>
          </div>
        </div>

        {/* How it works */}
        <div className="bg-gradient-to-br from-[#0A422D]/30 to-[#083323]/20 rounded-2xl border border-[#0A422D]/30 p-6 mb-6" data-testid="referral-how-it-works">
          <div className="flex items-center gap-2 mb-4">
            <Gift className="w-5 h-5 text-[#D4A853]" />
            <h2 className="text-base font-bold text-white">Comment ca marche ?</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="w-10 h-10 rounded-full bg-[#D4A853]/10 flex items-center justify-center mx-auto mb-2">
                <span className="text-[#D4A853] font-bold text-sm">1</span>
              </div>
              <p className="text-sm text-white font-medium">Partagez votre lien</p>
              <p className="text-xs text-[#888] mt-1">Envoyez-le a un collegue restaurateur</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 rounded-full bg-[#D4A853]/10 flex items-center justify-center mx-auto mb-2">
                <span className="text-[#D4A853] font-bold text-sm">2</span>
              </div>
              <p className="text-sm text-white font-medium">Il s'inscrit et s'abonne</p>
              <p className="text-xs text-[#888] mt-1">Via votre lien de parrainage</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 rounded-full bg-[#D4A853]/10 flex items-center justify-center mx-auto mb-2">
                <span className="text-[#D4A853] font-bold text-sm">3</span>
              </div>
              <p className="text-sm text-white font-medium">1 mois gratuit chacun</p>
              <p className="text-xs text-[#888] mt-1">Credite a la prochaine echeance</p>
            </div>
          </div>
        </div>

        {/* Referral Link */}
        <div className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] p-5 mb-6" data-testid="referral-link-section">
          <p className="text-sm text-[#888] mb-3">Votre lien de parrainage</p>
          <div className="flex gap-2">
            <div className="flex-1 bg-[#111] border border-[#333] rounded-xl px-4 py-3 text-sm text-white truncate font-mono" data-testid="referral-link-display">
              {referralUrl || 'Chargement...'}
            </div>
            <Button onClick={handleCopy} disabled={!referralUrl}
              className="bg-[#D4A853] hover:bg-[#C49A48] text-black font-bold rounded-xl px-4 shrink-0"
              data-testid="copy-referral-link">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-[#1A1A1A] rounded-xl border border-[#2A2A2A] p-4 text-center" data-testid="stat-total">
            <Users className="w-5 h-5 text-[#7C3AED] mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{stats?.total_referrals || 0}</p>
            <p className="text-xs text-[#888]">Filleuls invites</p>
          </div>
          <div className="bg-[#1A1A1A] rounded-xl border border-[#2A2A2A] p-4 text-center" data-testid="stat-subscribed">
            <CheckCircle className="w-5 h-5 text-green-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{stats?.subscribed || 0}</p>
            <p className="text-xs text-[#888]">Abonnes</p>
          </div>
          <div className="bg-[#1A1A1A] rounded-xl border border-[#2A2A2A] p-4 text-center" data-testid="stat-credits">
            <CreditCard className="w-5 h-5 text-[#D4A853] mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{stats?.credits_available || 0}</p>
            <p className="text-xs text-[#888]">Mois gratuits</p>
          </div>
        </div>

        {/* Referrals List */}
        {stats?.referrals?.length > 0 && (
          <div className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] overflow-hidden" data-testid="referrals-list">
            <div className="px-5 py-3 border-b border-[#2A2A2A]">
              <p className="text-sm font-medium text-white">Historique des parrainages</p>
            </div>
            <div className="divide-y divide-[#2A2A2A]">
              {stats.referrals.map((r, i) => (
                <div key={i} className="flex items-center justify-between px-5 py-3" data-testid={`referral-item-${i}`}>
                  <div>
                    <p className="text-sm text-white font-medium">{r.referred_name}</p>
                    <p className="text-xs text-[#888]">{r.referred_restaurant}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {r.status === 'subscribed' ? (
                      <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-green-500/10 text-green-400 text-xs font-medium">
                        <CheckCircle className="w-3 h-3" /> Abonne
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-[#D4A853]/10 text-[#D4A853] text-xs font-medium">
                        <Clock className="w-3 h-3" /> En attente
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {(!stats?.referrals || stats.referrals.length === 0) && (
          <div className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] p-8 text-center" data-testid="referral-empty">
            <Gift className="w-10 h-10 text-[#333] mx-auto mb-3" />
            <p className="text-sm text-[#888]">Aucun parrainage pour le moment</p>
            <p className="text-xs text-[#666] mt-1">Partagez votre lien pour commencer</p>
          </div>
        )}
      </div>
    </div>
  );
}
